import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Shield, Volume2, Square, ChevronDown, Lock, FileText, UserCheck, Cookie, Mail, Database } from "lucide-react";

const AUDIO_TEXT = `Bienvenue dans la version audio de la politique de confidentialité de Fodindin.
Fodindin protège vos données personnelles avec la plus grande rigueur, conformément aux principes du Règlement Général sur la Protection des Données, à la loi ivoirienne 2016-13 sur la protection des données, et aux directives de l'UEMOA.
Premièrement, vos données biométriques. Grâce au standard WebAuthn, votre empreinte digitale ne quitte jamais votre téléphone. Seule une clé cryptographique est enregistrée sur nos serveurs. Le selfie de référence anti-fraude est stocké de manière sécurisée et n'est jamais partagé.
Deuxièmement, vos données financières. Nous ne stockons jamais vos codes secrets Wave, Orange Money ou Moov Money. Les paiements passent par des passerelles sécurisées.
Troisièmement, vos droits. Vous pouvez consulter, corriger ou demander la suppression de vos données à tout moment, depuis vos paramètres ou en écrivant à support arobase fodindin point com. Nous répondons sous 30 jours maximum.
Quatrièmement, la sécurité. Toutes les communications sont chiffrées, vos mots de passe sont hachés et ne sont jamais lisibles, et notre système anti-fraude surveille les activités suspectes en continu.
Merci d'utiliser Fodindin en toute confiance. Vos données sont entre de bonnes mains.`;

const DATA_ROWS = [
  ["Identification", "Nom, téléphone, email, ville", "Création de compte, authentification", "Consentement"],
  ["Biométriques", "Clé cryptographique WebAuthn (l'empreinte reste sur votre téléphone), selfie de référence", "Sécurisation des paiements, anti-fraude", "Exécution du contrat"],
  ["Financières", "Numéros de compte Mobile Money (Wave, Orange, Moov, CinetPay) pour recevoir vos fonds", "Traitement des transactions", "Exécution du contrat"],
  ["Navigation", "Annonces consultées, favoris, messages", "Fonctionnement du service, personnalisation", "Intérêt légitime"],
];

const FAQ = [
  ["Quelles données biométriques stockez-vous ?", "Grâce au standard WebAuthn, votre empreinte digitale ne quitte JAMAIS votre téléphone : seule une clé cryptographique est enregistrée. Le selfie de référence anti-fraude est stocké de manière sécurisée et n'est jamais partagé avec des tiers."],
  ["Comment supprimer mes données ?", "Écrivez à support@fodindin.net depuis l'email de votre compte en demandant la suppression. Nous traitons votre demande sous 30 jours maximum, sauf obligation légale de conservation."],
  ["Qui a accès à mes données ?", "Uniquement l'équipe technique de Fodindin. Les passerelles de paiement (Wave, Orange Money, Moov, CinetPay) reçoivent seulement les informations nécessaires à vos transactions. Nous ne vendons jamais vos données."],
  ["Stockez-vous mes codes Mobile Money ?", "Non, jamais. Nous enregistrons uniquement le numéro de compte que vous choisissez pour RECEVOIR vos fonds. Vos codes secrets ne transitent jamais par Fodindin."],
  ["Combien de temps conservez-vous mes données ?", "Tant que votre compte est actif. Après suppression du compte, les données sont effacées sous 30 jours, sauf celles que la loi nous oblige à conserver (transactions)."],
  ["Puis-je utiliser Fodindin sans biométrie ?", "Oui. La biométrie est fortement recommandée pour sécuriser vos paiements, mais vous pouvez utiliser le code SMS (OTP) et votre mot de passe."],
  ["Que se passe-t-il en cas de fuite de données ?", "Nous vous notifierions dans les meilleurs délais par email et via l'application, conformément aux obligations légales, avec les mesures à prendre."],
  ["Puis-je obtenir une copie de mes données ?", "Oui, c'est votre droit à la portabilité. Écrivez à support@fodindin.net : nous vous transmettons vos données dans un format lisible sous 30 jours."],
];

export default function PolitiqueConfidentialitePage() {
  const [speaking, setSpeaking] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => () => window.speechSynthesis?.cancel(), []);

  const toggleAudio = () => {
    if (!("speechSynthesis" in window)) return;
    if (speaking) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
      return;
    }
    const u = new SpeechSynthesisUtterance(AUDIO_TEXT);
    u.lang = "fr-FR";
    u.rate = 0.95;
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    window.speechSynthesis.speak(u);
    setSpeaking(true);
  };

  return (
    <main className="max-w-4xl mx-auto px-6 py-10" data-testid="confidentialite-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight inline-flex items-center gap-3">
            <Shield size={32} className="text-emerald-600" /> Politique de confidentialité
          </h1>
          <p className="text-[var(--ink-soft)] mt-2 text-sm">
            Dernière mise à jour : 5 juillet 2026 — Conçue selon les principes du RGPD, de la loi ivoirienne n°2013-450
            relative à la protection des données à caractère personnel et des directives UEMOA.
          </p>
        </div>
        <button
          type="button"
          onClick={toggleAudio}
          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-bold transition-colors ${
            speaking ? "bg-red-600 text-white hover:bg-red-700" : "bg-emerald-600 text-white hover:bg-emerald-700"
          }`}
          data-testid="rgpd-audio-btn"
          title="Version audio pour malvoyants — lecture à voix haute"
        >
          {speaking ? <Square size={15} /> : <Volume2 size={15} />}
          {speaking ? "Arrêter la lecture" : "Écouter cette page"}
        </button>
      </div>

      <Section icon={FileText} title="1. Introduction">
        <p>
          Chez <b>Fodindin</b>, la protection de vos données personnelles est une priorité absolue. Cette politique
          explique comment nous collectons, utilisons et protégeons vos données. En utilisant fodindin.com, vous
          acceptez les termes de cette politique, complémentaire de nos <Link to="/cgu" className="text-orange-600 font-semibold">CGU</Link> et
          de notre page <Link to="/confiance-securite" className="text-orange-600 font-semibold">Confiance & Sécurité</Link>.
        </p>
      </Section>

      <Section icon={Database} title="2. Données que nous collectons">
        <p className="mb-3">Nous collectons uniquement les données nécessaires au fonctionnement du service :</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse" data-testid="rgpd-data-table">
            <thead>
              <tr className="bg-emerald-50 text-left">
                {["Type", "Exemples", "Finalité", "Base légale"].map((h) => (
                  <th key={h} className="border border-[var(--line)] px-3 py-2 font-bold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {DATA_ROWS.map((row) => (
                <tr key={row[0]}>
                  {row.map((cell, i) => (
                    <td key={i} className={`border border-[var(--line)] px-3 py-2 ${i === 0 ? "font-semibold" : "text-[var(--ink-soft)]"}`}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm">
          <b>⚠️ Nous ne collectons jamais :</b> vos codes secrets Mobile Money, vos données bancaires complètes,
          ni votre empreinte digitale elle-même (elle reste sur votre téléphone grâce au standard WebAuthn).
        </div>
      </Section>

      <Section icon={Lock} title="3. Sécurité de vos données">
        <ul className="list-disc pl-5 space-y-2">
          <li><b>Chiffrement en transit</b> : toutes les communications passent par HTTPS/TLS, comme les banques.</li>
          <li><b>Mots de passe hachés</b> : ils ne sont jamais stockés ni lisibles en clair.</li>
          <li><b>Biométrie WebAuthn</b> : votre empreinte ne quitte jamais votre appareil ; seule une clé cryptographique publique est enregistrée.</li>
          <li><b>Anti-fraude actif</b> : surveillance des activités suspectes, limitation des tentatives (rate limiting), journal de sécurité.</li>
          <li><b>Notification d'incident</b> : en cas de violation de données, vous seriez informé dans les meilleurs délais conformément à la loi.</li>
        </ul>
      </Section>

      <Section icon={UserCheck} title="4. Vos droits">
        <p className="mb-3">Conformément au RGPD et à la législation ivoirienne, vous disposez des droits suivants :</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            ["🔍 Droit d'accès", "Demandez une copie de vos données à tout moment."],
            ["✏️ Droit de rectification", "Corrigez vos informations depuis vos Paramètres."],
            ["🗑️ Droit à l'effacement", "Demandez la suppression de votre compte et de vos données."],
            ["🔄 Droit à la portabilité", "Récupérez vos données dans un format lisible."],
            ["🚫 Droit d'opposition", "Refusez les communications marketing."],
            ["⚖️ Droit de limitation", "Limitez le traitement de vos données dans certains cas."],
          ].map(([t, d]) => (
            <div key={t} className="rounded-2xl border border-[var(--line)] bg-white/70 p-3">
              <div className="font-bold text-sm">{t}</div>
              <p className="text-xs text-[var(--ink-soft)] mt-1">{d}</p>
            </div>
          ))}
        </div>
        <p className="mt-4 text-sm">
          <b>Pour exercer vos droits :</b> écrivez à{" "}
          <a href="mailto:support@fodindin.net" className="text-orange-600 font-semibold" data-testid="rgpd-contact-mail">support@fodindin.net</a>{" "}
          depuis l'adresse email de votre compte. Réponse sous <b>30 jours maximum</b>.
        </p>
      </Section>

      <Section icon={Cookie} title="5. Cookies & stockage local">
        <p>
          Fodindin utilise uniquement des <b>cookies essentiels</b> (session de connexion sécurisée) et le
          <b> stockage local</b> de votre navigateur (préférences de langue, progression du guide biométrique,
          consentement). Nous n'utilisons <b>aucun cookie publicitaire tiers</b>.
        </p>
      </Section>

      <Section icon={Mail} title="6. Contact & réclamations">
        <p>
          Pour toute question sur vos données : <a href="mailto:support@fodindin.net" className="text-orange-600 font-semibold">support@fodindin.net</a>.
          En cas de litige non résolu, vous pouvez saisir l'<b>ARTCI</b> (Autorité de Régulation des Télécommunications
          de Côte d'Ivoire), autorité de protection des données personnelles.
        </p>
      </Section>

      <section className="mt-10" data-testid="rgpd-faq">
        <h2 className="font-display text-2xl font-extrabold tracking-tight mb-4">❓ Questions fréquentes</h2>
        <div className="space-y-2">
          {FAQ.map(([q, a], i) => (
            <div key={i} className="rounded-2xl border border-[var(--line)] bg-white/70 overflow-hidden">
              <button
                type="button"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left font-semibold text-sm hover:bg-emerald-50/50 transition-colors"
                data-testid={`rgpd-faq-q-${i}`}
              >
                {q}
                <ChevronDown size={16} className={`shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
              </button>
              {openFaq === i && (
                <p className="px-4 pb-4 text-sm text-[var(--ink-soft)] leading-relaxed" data-testid={`rgpd-faq-a-${i}`}>{a}</p>
              )}
            </div>
          ))}
        </div>
      </section>

      <p className="mt-10 text-center text-sm text-[var(--ink-soft)]">
        Votre confiance est notre plus grande richesse. — <b>L'équipe Fodindin</b>
      </p>
    </main>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <section className="mt-8">
      <h2 className="font-display text-xl font-extrabold tracking-tight mb-3 inline-flex items-center gap-2">
        <Icon size={20} className="text-emerald-600" /> {title}
      </h2>
      <div className="text-sm leading-relaxed text-[var(--ink)]">{children}</div>
    </section>
  );
}
